import { memo, useCallback, type ChangeEvent } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import type { FormFieldBaseProps } from './field-base';

export interface RadioFieldProps extends FormFieldBaseProps<string | null | undefined> {
  /**
   * Override the HTML radio group `name`. Defaults to `field.key`. Inside a
   * table, each row needs an independent radio group (same column key across
   * rows would otherwise let the browser uncheck radios in OTHER rows when
   * one is clicked) — TableField passes a per-row name to scope it.
   */
  name?: string;
}

export const RadioField = memo(function RadioField({
  field,
  value,
  onChange,
  readOnly = false,
  name,
  error,
}: RadioFieldProps) {
  const { theme } = useTheme();
  const options = field.options ?? [];
  const groupName = name ?? field.key;

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  const errorId = error ? `${field.key}__error` : undefined;
  return (
    <div className={'form-group radio-field' + (theme ? ' ' + theme : '') + (error ? ' has-error' : '')}>
      <label className={'field-label' + (field.required ? ' required' : '')}>
        {field.label}
      </label>
      <div className="radio-options" role="radiogroup" aria-invalid={error ? true : undefined} aria-describedby={errorId}>
        {options.map((option) => (
          <label key={option.value} className="radio-option">
            <input
              type="radio"
              name={groupName}
              value={option.value}
              checked={value === option.value}
              onChange={handleChange}
              disabled={readOnly}
            />
            {' '}
            {option.label}
          </label>
        ))}
      </div>
      {error ? <div id={errorId} className="form-field-error" role="alert">{error}</div> : null}
    </div>
  );
});
